/**
 * Mapeia e deduplica mensagens do array cru (Baileys) para formato interno UazapiMessage.
 *
 * Princípios: Single Responsibility (Isola regras de parsing de dados do Whatsapp).
 */
export interface MappedMessage {
  messageid: string;
  chatid: string;
  text: string;
  sender: string;
  fromMe: boolean;
  messageType: string;
  messageTimestamp: number;
  fileURL: string | null;
  owner: string;
  quoted: string | null;
  key: any;
}

export async function mapAndFilterMessages(
  chatMessages: any[],
  messagesPerChat: number,
  ownerJid: string,
  instanceName: string,
): Promise<MappedMessage[]> {
  // 1. Ordena TUDO por timestamp cronologicamente (ASC) PRIMEIRO, para que as msgs mais antigas fiquem no início
  const sortedMessages = [...chatMessages].sort((a, b) => {
    const tsA = a.messageTimestamp || a.timestamp || 0;
    const tsB = b.messageTimestamp || b.timestamp || 0;
    return tsA - tsB;
  });

  // 2. Limita pegando apenas a quantidade configurada **DAS MAIS RECENTES** (ou seja, corto o início histórico distante)
  const startIndex = Math.max(0, sortedMessages.length - messagesPerChat);
  const limitedMessages = sortedMessages.slice(startIndex);

  // Mídias do histórico ficam com URL temporária — webhook/R2 cuidam das novas mensagens
  const mappedRaw = limitedMessages
    .map((msg) => {
      if (msg.messageid) return msg;
      const key = msg.key || {};
      const message = msg.message || {};
      const messageType =
        Object.keys(message).find((k) => k !== "messageContextInfo") ||
        "conversation";
      const contentObj = message[messageType] || message;

      let text =
        typeof contentObj === "string"
          ? contentObj
          : contentObj?.text || contentObj?.caption || "";

      if (messageType === "conversation") {
        text =
          typeof message.conversation === "string"
            ? message.conversation
            : text;
      } else if (messageType === "extendedTextMessage") {
        text = message.extendedTextMessage?.text || text;
      }

      // Ajuste para não enviar mensagens vazias pro DB/Frontend (proteção contra quebra visual)
      if (!text || text.trim() === "") {
        if (
          msg.fileURL ||
          msg.mediaURL ||
          messageType.includes("Image") ||
          messageType.includes("Video") ||
          messageType.includes("Document") ||
          messageType.includes("Audio") ||
          messageType.includes("Sticker")
        ) {
          text = `[${messageType.replace("Message", " de Mídia")}]`;
        } else {
          text = `[Mensagem do Sistema ou Incompatível]`;
        }
      }

      const actualFromMe =
        key.fromMe === true ||
        msg.fromMe === true ||
        msg.fromMe === 1 ||
        msg.fromMe === "true";

      return {
        messageid: key.id || msg.id,
        chatid: key.remoteJid,
        text,
        sender: msg.participant || (actualFromMe ? ownerJid : key.remoteJid),
        fromMe: actualFromMe,
        messageType:
          messageType.charAt(0).toUpperCase() +
          messageType.slice(1) +
          (messageType.endsWith("Message") ? "" : "Message"),
        messageTimestamp:
          msg.messageTimestamp ||
          msg.timestamp ||
          Math.floor(Date.now() / 1000),
        fileURL: msg.fileURL || null,
        owner: msg.owner || ownerJid,
        quoted: contentObj?.contextInfo?.stanzaId || null,
        key,
      };
    })
    .filter((m) => m.messageid);

  // 5. Deduplicação em memória do mesmo sync.
  // Mídia (álbuns) pode compartilhar texto placeholder — chaveia por messageid.
  // Texto usa janela de 5s + prefixo para evitar revives da mesma msg no sync.
  const mapped: MappedMessage[] = [];
  const seenKeys = new Set<string>();
  const mediaTypes = new Set([
    'ImageMessage', 'VideoMessage', 'AudioMessage', 'PttMessage',
    'DocumentMessage', 'StickerMessage',
  ]);

  for (const m of mappedRaw) {
    const isMedia = mediaTypes.has(m.messageType) || !!m.fileURL;
    const tsWindow = Math.floor(m.messageTimestamp / 5);
    const uniqKey = isMedia
      ? `id:${m.messageid}`
      : `${m.text?.substring(0, 30)}_${tsWindow}`;
    if (!seenKeys.has(uniqKey) || !m.text) {
      seenKeys.add(uniqKey);
      mapped.push(m);
    }
  }

  return mapped;
}
